import React from 'react';
import {
  ArrowRight,
  Users,
  Building,
  DollarSign,
  Percent,
  Bell,
  Newspaper,
  Server,
  Lock,
  Activity,
  UserCheck,
  PieChart,
  Smartphone
} from 'lucide-react';
import { useNavigate, Navigate } from 'react-router-dom';
import '../styles/Management.css';
import { haptic } from '../utils/vibration';

const Management = ({ userType: propUserType }) => {
  const navigate = useNavigate();

  // Приоритетно берём userType из пропсов (из App), иначе из localStorage как fallback
  const userType = propUserType ?? localStorage.getItem('user_type');

  // Чистый редирект без промежуточного UI, чтобы избежать миганий
  if (userType !== 'admin' && userType !== 'developer') {
    return <Navigate to="/" replace />;
  }

  // Конфигурация разделов управления
  const managementSections = [
    {
      id: 'users',
      title: 'Пользователи',
      description: 'Создание и управление пользователями',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      path: '/management/users',
      accessLevel: ['admin', 'developer']
    },
    {
      id: 'departments',
      title: 'Подразделения',
      description: 'Управление подразделениями',
      icon: Building,
      // Changed to reddish gradient
      color: 'from-red-500 to-orange-600',
      path: '/management/departments',
      accessLevel: ['admin', 'developer']
    },
    {
      id: 'services',
      title: 'Услуги и цены',
      description: 'Прайс-лист и ценообразование',
      icon: DollarSign,
      color: 'from-green-500 to-green-600',
      path: '/management/services',
      accessLevel: ['admin', 'developer']
    },
    {
      id: 'client-app',
      title: 'Клиентское приложение',
      description: 'Витрина и управление клиентским контуром',
      icon: Smartphone,
      color: 'from-blue-600 to-cyan-500',
      path: '/management/client-app',
      accessLevel: ['developer']
    },
    {
      id: 'clients',
      title: 'Клиенты',
      description: 'Управление клиентской базой',
      icon: UserCheck,
      color: 'from-teal-500 to-cyan-600',
      path: '/management/clients',
      accessLevel: ['admin', 'developer']
    },
    {
      id: 'profit-radar',
      title: 'Радар прибыли',
      description: 'Прибыльность заказов, клиентов, услуг и маршрутов',
      icon: PieChart,
      color: 'from-emerald-500 to-teal-700',
      path: '/management/profit-radar',
      accessLevel: ['developer']
    },
    {
      id: 'activity',
      title: 'Активность',
      description: 'Лог действий пользователей',
      icon: Activity,
      color: 'from-purple-500 to-indigo-600',
      path: '/management/activity',
      accessLevel: ['developer']
    },
    {
      id: 'discounts',
      title: 'Скидки и акции',
      description: 'Скидки заказа и акции прайса',
      icon: Percent,
      color: 'from-purple-500 to-purple-600',
      path: '/management/discounts',
      accessLevel: ['admin', 'developer']
    },
    {
      id: 'news',
      title: 'Публикации',
      description: 'Публикации приложения и служебные объявления',
      icon: Newspaper,
      color: 'from-sky-500 to-blue-600',
      path: '/management/news',
      accessLevel: ['developer']
    },
    {
      id: 'notifications',
      title: 'Уведомления',
      description: 'Отправка массовых и индивидуальных уведомлений',
      icon: Bell,
      color: 'from-yellow-500 to-yellow-600',
      path: '/management/notifications',
      accessLevel: ['admin', 'developer']
    },
    {
      id: 'system',
      title: 'Система',
      description: userType === 'developer' ? 'API токены, адреса, настройки' : 'Организация, параметры, ячейки склада',
      icon: Server,
      color: 'from-orange-500 to-orange-600',
      path: '/management/system',
      accessLevel: ['admin', 'developer']
    },
    {
      id: 'security',
      title: 'Безопасность',
      description: 'Мониторинг и анализ угроз',
      icon: Lock,
      color: 'from-red-500 to-red-600',
      path: '/management/security',
      accessLevel: ['developer']
    }
  ];

  // Фильтруем разделы по уровню доступа
  const availableSections = managementSections.filter(section => 
    section.accessLevel.includes(userType)
  );

  const handleSectionClick = (path) => {
    haptic('selection');
    navigate(path);
  };

  return (
    <div className="flex flex-col gap-3 animate-fade-in relative z-0 px-4 pt-4 desktop-content-container-wide">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 relative z-0">
        {availableSections.map((section, index) => {
          const Icon = section.icon;
          return (
            <div
              key={section.id}
              onClick={() => handleSectionClick(section.path)}
              className={`group relative overflow-hidden bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer section-card animate-slide-up`}
              style={{ animationDelay: `${0.2 + index * 0.1}s` }}
            >
              {/* Градиентный фон при наведении */}
              <div className={`absolute inset-0 bg-gradient-to-br ${section.color} section-bg-gradient opacity-0`} />
              
              {/* Основной контент */}
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br ${section.color} rounded-lg section-icon`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 section-title leading-tight">
                      {section.title}
                    </h3>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 section-arrow" />
                </div>
                
                <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed ml-11 section-description">
                  {section.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Management;
